import { type ComponentPropsWithRef } from "react";
import {
  Provider as TooltipProviderPrimitive,
  Root as TooltipRoot,
  Trigger as TooltipTriggerPrimitive,
  Content as TooltipContentPrimitive,
  Portal,
} from "@radix-ui/react-tooltip";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { CONSOLE_TOOLTIP_SURFACE_CLASS } from "@/lib/ui/consoleTooltipSurface";

const TooltipProvider = TooltipProviderPrimitive;

const Tooltip = TooltipRoot;

const TooltipTrigger = TooltipTriggerPrimitive;

export type TooltipContentLayout = NonNullable<
  VariantProps<typeof tooltipContentVariants>["layout"]
>;

const tooltipContentVariants = cva(
  `
    z-50 origin-[--radix-tooltip-content-transform-origin] scale-100
    ${CONSOLE_TOOLTIP_SURFACE_CLASS}
    opacity-100 transition-[opacity,transform] duration-200
    data-[side=bottom]:slide-in-from-top-2
    data-[side=left]:slide-in-from-right-2
    data-[side=right]:slide-in-from-left-2
    data-[side=top]:slide-in-from-bottom-2
    data-[state=closed]:scale-95 data-[state=closed]:opacity-0
  `,
  {
    variants: {
      layout: {
        default: "max-w-sm leading-snug",
        inline: "",
      },
    },
    defaultVariants: {
      layout: "default",
    },
  },
);

const TooltipContent = ({
  className,
  layout = "default",
  sideOffset = 4,
  collisionPadding = 8,
  ref,
  ...props
}: ComponentPropsWithRef<typeof TooltipContentPrimitive> &
  VariantProps<typeof tooltipContentVariants> & {
    layout?: TooltipContentLayout;
  }) => (
  <Portal>
    <TooltipContentPrimitive
      ref={ref}
      sideOffset={sideOffset}
      collisionPadding={collisionPadding}
      data-slot="tooltip-content"
      className={cn(tooltipContentVariants({ layout }), className)}
      {...props}
    />
  </Portal>
);
TooltipContent.displayName = TooltipContentPrimitive.displayName;

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  tooltipContentVariants,
};
